import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";

const ChecklistManagementModal = ({
  show,
  onClose,
  onSuccess,
  facilityId,
  roster,
  staffUsers,
}) => {
  const [loading, setLoading] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [todaysTasks, setTodaysTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskUpdateModal, setShowTaskUpdateModal] = useState(false);
  const [taskUpdateData, setTaskUpdateData] = useState({
    status: "completed",
    notes: "",
    startTime: "",
    endTime: "",
    completedBy: "",
  });

  useEffect(() => {
    if (show && roster) {
      fetchChecklistData();
      fetchTodaysTasks();
    }
  }, [show, roster]);

  const fetchChecklistData = async () => {
    try {
      setLoading(true);
      console.log("Fetching checklist for roster:", roster._id);

      const response = await makeRequest2(
        `/api/duty_roster_checklist/${facilityId}/duty-roster/${roster._id}?includeTaskDetails=true&includeChildWorkplanDetails=true`,
        "GET",
      );

      console.log("Checklist response:", response);

      if (response && response.success) {
        // Handle nested data structure: response.data.data.checklist or response.data.checklist
        let checklistData = null;

        if (response.data?.data?.checklist) {
          checklistData = response.data.data.checklist;
          console.log("Found checklist in response.data.data.checklist");
        } else if (response.data?.checklist) {
          checklistData = response.data.checklist;
          console.log("Found checklist in response.data.checklist");
        }

        if (checklistData) {
          console.log("Setting checklist data:", checklistData._id);
          setChecklist(checklistData);
          return checklistData; // Return the checklist for immediate use
        } else {
          console.log("No checklist data found in response");
          setChecklist(null);
          return null;
        }
      } else {
        console.log("Response not successful or no data");
        setChecklist(null);
        return null;
      }
    } catch (error) {
      console.error("Error fetching checklist:", error);
      // Check if it's a 404 error (checklist not found)
      if (error.response?.status === 404) {
        console.log("404 error - checklist not found");
        setChecklist(null);
      } else {
        console.log("Other error:", error.message);
        setChecklist(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaysTasks = async () => {
    try {
      // Extract staffId properly
      const staffId =
        roster.staffId?._id || roster.staffId || roster.staffDetails?._id;

      if (!staffId) {
        console.error("No staff ID found in roster:", roster);
        setTodaysTasks([]);
        return;
      }

      console.log("Fetching today's tasks for staff:", staffId);

      const response = await makeRequest2(
        `/api/duty_roster_checklist/${facilityId}/staff/${staffId}/today?includeChildWorkplanDetails=true`,
        "GET",
      );

      console.log("Today's tasks response:", response);

      if (response && response.success) {
        // Handle nested data structure
        let tasksData = null;

        if (response.data?.todaysTasks) {
          tasksData = response.data.todaysTasks;
        } else if (response.data?.data?.todaysTasks) {
          tasksData = response.data.data.todaysTasks;
        }

        setTodaysTasks(tasksData || []);
      } else {
        setTodaysTasks([]);
      }
    } catch (error) {
      console.error("Error fetching today's tasks:", error);
      setTodaysTasks([]);
    }
  };

  // Function to create missing checklist
  const handleCreateChecklist = async () => {
    try {
      setLoading(true);

      console.log("Creating checklist for duty roster:", roster._id);

      // First check if checklist already exists by calling the fetch function
      const existingChecklist = await fetchChecklistData();

      // If checklist was found, don't create a new one
      if (existingChecklist) {
        console.log("Checklist already exists, not creating new one");
        toastify("Checklist already exists and has been loaded!", "info");
        return;
      }

      console.log("No existing checklist found, creating new one...");

      // Call the create missing checklist endpoint
      const response = await makeRequest2(
        `/api/duty_roster_checklist/${facilityId}/duty-roster/${roster._id}/create`,
        "POST",
        {
          taskFrequency: "daily", // You can make this configurable
        },
      );

      console.log("Create checklist response:", response);

      if (response && response.success) {
        toastify("Checklist created successfully!", "success");
        // Refresh the checklist data
        await fetchChecklistData();
        await fetchTodaysTasks();
        // Also call the parent success handler to refresh other data
        onSuccess();
      } else {
        toastify(response?.error || "Failed to create checklist", "error");
      }
    } catch (error) {
      console.error("Error creating checklist:", error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        console.log("409 error - checklist already exists, refreshing data...");
        toastify("Checklist already exists for this duty roster", "info");
        // Refresh data to show the existing checklist
        await fetchChecklistData();
        await fetchTodaysTasks();
      } else {
        toastify("Error creating checklist", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTaskStatusUpdate = async (task, dateInfo) => {
    setSelectedTask({ task, dateInfo });

    // Auto-populate completedBy with the checklist's staffId when marking as completed
    const defaultCompletedBy =
      dateInfo.status === "pending"
        ? checklist?.staffId || ""
        : dateInfo.completedBy || "";

    setTaskUpdateData({
      status: dateInfo.status === "pending" ? "completed" : dateInfo.status,
      notes: dateInfo.notes || "",
      startTime: dateInfo.startTime || "",
      endTime: dateInfo.endTime || "",
      completedBy: defaultCompletedBy,
    });
    setShowTaskUpdateModal(true);
  };

  const submitTaskUpdate = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/duty_roster_checklist/${facilityId}/${checklist._id}/task/${selectedTask.task._id}/date/${selectedTask.dateInfo._id}/status`,
        "PATCH",
        taskUpdateData,
      );

      if (response && response.success) {
        toastify("Task status updated successfully", "success");
        fetchChecklistData();
        fetchTodaysTasks();
        setShowTaskUpdateModal(false);
      } else {
        toastify("Failed to update task status", "error");
      }
    } catch (error) {
      toastify("Error updating task status", "error");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed":
        return "bg-success";
      case "pending":
        return "bg-warning";
      case "in-progress":
        return "bg-primary";
      case "missed":
        return "bg-danger";
      case "cancelled":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case "critical":
        return "bg-danger";
      case "high":
        return "bg-warning";
      case "medium":
        return "bg-primary";
      case "low":
        return "bg-success";
      default:
        return "bg-secondary";
    }
  };

  if (!show || !roster) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Checklist Management - {roster.staffDetails?.fullName || "Staff"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading checklist data...</p>
              </div>
            ) : !checklist ? (
              <div className="text-center py-5">
                <i
                  className="ti ti-clipboard-off"
                  style={{ fontSize: "48px", color: "#ccc" }}
                ></i>
                <h6 className="mt-3">No Checklist Found</h6>
                <p className="text-muted">
                  This duty roster doesn't have an associated checklist yet.
                </p>
                {/* Create Checklist Button */}
                <button
                  className="btn btn-primary"
                  onClick={handleCreateChecklist}
                  disabled={loading}
                >
                  {loading && (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  )}
                  <i className="ti ti-plus me-1"></i>
                  Create Checklist
                </button>
                <div className="mt-3">
                  <small className="text-muted">
                    This will create a daily task checklist based on the child
                    tasks in the assigned master workplan:{" "}
                    <strong>{roster.masterWorkplanDetails?.title}</strong>
                  </small>
                </div>
              </div>
            ) : (
              <>
                {/* Tabs Navigation */}
                <ul className="nav nav-tabs mb-4">
                  <li className="nav-item">
                    <a
                      className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
                      onClick={() => setActiveTab("overview")}
                      href="#overview"
                    >
                      <i className="ti ti-dashboard me-2"></i>
                      Overview
                    </a>
                  </li>
                  <li className="nav-item">
                    <a
                      className={`nav-link ${activeTab === "tasks" ? "active" : ""}`}
                      onClick={() => setActiveTab("tasks")}
                      href="#tasks"
                    >
                      <i className="ti ti-list-check me-2"></i>
                      All Tasks
                    </a>
                  </li>
                  <li className="nav-item">
                    <a
                      className={`nav-link ${activeTab === "today" ? "active" : ""}`}
                      onClick={() => setActiveTab("today")}
                      href="#today"
                    >
                      <i className="ti ti-calendar-today me-2"></i>
                      Today's Tasks ({todaysTasks.length})
                    </a>
                  </li>
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                  {/* Overview Tab */}
                  {activeTab === "overview" && (
                    <div className="tab-pane show active">
                      <div className="row">
                        {/* Progress Summary */}
                        <div className="col-md-8">
                          <div className="card bg-light">
                            <div className="card-body">
                              <h6 className="card-title">Progress Summary</h6>
                              <div className="row text-center mb-3">
                                <div className="col-3">
                                  <h4 className="text-success mb-0">
                                    {checklist.summary?.completedTasks || 0}
                                  </h4>
                                  <small>Completed</small>
                                </div>
                                <div className="col-3">
                                  <h4 className="text-warning mb-0">
                                    {checklist.summary?.pendingTasks || 0}
                                  </h4>
                                  <small>Pending</small>
                                </div>
                                <div className="col-3">
                                  <h4 className="text-danger mb-0">
                                    {checklist.summary?.missedTasks || 0}
                                  </h4>
                                  <small>Missed</small>
                                </div>
                                <div className="col-3">
                                  <h4 className="text-primary mb-0">
                                    {checklist.summary?.totalTasks || 0}
                                  </h4>
                                  <small>Total</small>
                                </div>
                              </div>
                              <div
                                className="progress mb-2"
                                style={{ height: "25px" }}
                              >
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${checklist.summary?.completionPercentage || 0}%`,
                                  }}
                                >
                                  {checklist.summary?.completionPercentage || 0}
                                  %
                                </div>
                              </div>
                              <div className="d-flex justify-content-between">
                                <small>Overall Progress</small>
                                <small>
                                  {checklist.summary?.completedTasks || 0} of{" "}
                                  {checklist.summary?.totalTasks || 0} tasks
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="col-md-4">
                          <div className="card bg-light">
                            <div className="card-body">
                              <h6 className="card-title">Quick Stats</h6>
                              <ul className="list-unstyled mb-0">
                                <li className="d-flex justify-content-between">
                                  <span>Period:</span>
                                  <span>
                                    {checklist.period ? (
                                      <>
                                        {new Date(
                                          checklist.period.startDate,
                                        ).toLocaleDateString()}{" "}
                                        -{" "}
                                        {new Date(
                                          checklist.period.endDate,
                                        ).toLocaleDateString()}
                                      </>
                                    ) : (
                                      "Not set"
                                    )}
                                  </span>
                                </li>
                                <li className="d-flex justify-content-between">
                                  <span>Status:</span>
                                  <span
                                    className={`badge ${getStatusBadgeClass(checklist.status)}`}
                                  >
                                    {checklist.status}
                                  </span>
                                </li>
                                <li className="d-flex justify-content-between">
                                  <span>Task Types:</span>
                                  <span>{checklist.tasks?.length || 0}</span>
                                </li>
                                <li className="d-flex justify-content-between">
                                  <span>Today's Tasks:</span>
                                  <span className="badge bg-primary">
                                    {todaysTasks.length}
                                  </span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Recent Activity */}
                      <div className="row mt-4">
                        <div className="col-12">
                          <h6>Recent Activity</h6>
                          <div className="card">
                            <div className="card-body">
                              {checklist.tasks && checklist.tasks.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table table-sm">
                                    <thead>
                                      <tr>
                                        <th>Task</th>
                                        <th>Priority</th>
                                        <th>Total Scheduled</th>
                                        <th>Completed</th>
                                        <th>Pending</th>
                                        <th>Progress</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {checklist.tasks
                                        .slice(0, 5)
                                        .map((task) => {
                                          const completed =
                                            task.scheduledDates?.filter(
                                              (d) => d.status === "completed",
                                            ).length || 0;
                                          const total =
                                            task.scheduledDates?.length || 0;
                                          const progress =
                                            total > 0
                                              ? Math.round(
                                                  (completed / total) * 100,
                                                )
                                              : 0;

                                          return (
                                            <tr key={task._id}>
                                              <td>
                                                <strong>
                                                  {task.childWorkplanDetails
                                                    ?.name || "Task"}
                                                </strong>
                                                <br />
                                                <small className="text-muted">
                                                  {task.childWorkplanDetails?.description?.substring(
                                                    0,
                                                    50,
                                                  ) || "No description"}
                                                  {task.childWorkplanDetails
                                                    ?.description?.length > 50
                                                    ? "..."
                                                    : ""}
                                                </small>
                                              </td>
                                              <td>
                                                <span
                                                  className={`badge ${getPriorityBadgeClass(task.priority)}`}
                                                >
                                                  {task.priority}
                                                </span>
                                              </td>
                                              <td>{total}</td>
                                              <td className="text-success">
                                                {completed}
                                              </td>
                                              <td className="text-warning">
                                                {total - completed}
                                              </td>
                                              <td>
                                                <div
                                                  className="progress"
                                                  style={{
                                                    height: "15px",
                                                    width: "80px",
                                                  }}
                                                >
                                                  <div
                                                    className="progress-bar"
                                                    style={{
                                                      width: `${progress}%`,
                                                    }}
                                                  >
                                                    {progress}%
                                                  </div>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-muted">No tasks available</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* All Tasks Tab */}
                  {activeTab === "tasks" && (
                    <div className="tab-pane show active">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6>All Tasks ({checklist.tasks?.length || 0})</h6>
                        <div className="d-flex gap-2">
                          <select
                            className="form-select form-select-sm"
                            style={{ width: "auto" }}
                          >
                            <option value="">All Priorities</option>
                            <option value="critical">Critical</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: "auto" }}
                          >
                            <option value="">All Statuses</option>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                            <option value="in-progress">In Progress</option>
                            <option value="missed">Missed</option>
                          </select>
                        </div>
                      </div>

                      <div className="row">
                        {checklist.tasks?.map((task) => (
                          <div key={task._id} className="col-12 mb-3">
                            <div className="card">
                              <div className="card-header d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 className="mb-0">
                                    {task.childWorkplanDetails?.name || "Task"}
                                  </h6>
                                  <small className="text-muted">
                                    {task.childWorkplanDetails?.description ||
                                      "No description"}
                                  </small>
                                </div>
                                <div className="d-flex gap-2">
                                  <span
                                    className={`badge ${getPriorityBadgeClass(task.priority)}`}
                                  >
                                    {task.priority}
                                  </span>
                                  {task.isRecurring && (
                                    <span className="badge bg-info">
                                      {task.recurringPattern}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="card-body">
                                <div className="row">
                                  {task.scheduledDates
                                    ?.slice(0, 10)
                                    .map((dateInfo, index) => (
                                      <div
                                        key={dateInfo._id}
                                        className="col-md-6 col-lg-4 mb-2"
                                      >
                                        <div
                                          className={`border rounded p-2 cursor-pointer ${
                                            dateInfo.status === "completed"
                                              ? "border-success bg-light"
                                              : dateInfo.status === "pending"
                                                ? "border-warning"
                                                : dateInfo.status === "missed"
                                                  ? "border-danger"
                                                  : "border-secondary"
                                          }`}
                                          onClick={() =>
                                            handleTaskStatusUpdate(
                                              task,
                                              dateInfo,
                                            )
                                          }
                                        >
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                              <strong>
                                                {new Date(
                                                  dateInfo.date,
                                                ).toLocaleDateString()}
                                              </strong>
                                              {dateInfo.startTime && (
                                                <div>
                                                  <small>
                                                    {dateInfo.startTime} -{" "}
                                                    {dateInfo.endTime}
                                                  </small>
                                                </div>
                                              )}
                                            </div>
                                            <span
                                              className={`badge ${getStatusBadgeClass(dateInfo.status)}`}
                                            >
                                              {dateInfo.status}
                                            </span>
                                          </div>
                                          {dateInfo.notes && (
                                            <small className="text-muted d-block mt-1">
                                              {dateInfo.notes.substring(0, 30)}
                                              {dateInfo.notes.length > 30
                                                ? "..."
                                                : ""}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                                {(task.scheduledDates?.length || 0) > 10 && (
                                  <div className="text-center mt-2">
                                    <small className="text-muted">
                                      ... and{" "}
                                      {(task.scheduledDates?.length || 0) - 10}{" "}
                                      more dates
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Today's Tasks Tab */}
                  {activeTab === "today" && (
                    <div className="tab-pane show active">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6>Today's Tasks ({todaysTasks.length})</h6>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={fetchTodaysTasks}
                        >
                          <i className="ti ti-refresh me-1"></i>
                          Refresh
                        </button>
                      </div>

                      {todaysTasks.length === 0 ? (
                        <div className="text-center py-5">
                          <i
                            className="ti ti-calendar-check"
                            style={{ fontSize: "48px", color: "#ccc" }}
                          ></i>
                          <h6 className="mt-3">No Tasks for Today</h6>
                          <p className="text-muted">
                            Great! No tasks scheduled for today or all tasks are
                            completed.
                          </p>
                        </div>
                      ) : (
                        <div className="row">
                          {todaysTasks.map((task) => (
                            <div
                              key={`${task.taskId}-${task.dateId}`}
                              className="col-md-6 mb-3"
                            >
                              <div className="card h-100">
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="card-title mb-0">
                                      {task.childWorkplanDetails?.name ||
                                        "Task"}
                                    </h6>
                                    <div className="d-flex gap-1">
                                      <span
                                        className={`badge ${getPriorityBadgeClass(task.priority)}`}
                                      >
                                        {task.priority}
                                      </span>
                                      <span
                                        className={`badge ${getStatusBadgeClass(task.status)}`}
                                      >
                                        {task.status}
                                      </span>
                                    </div>
                                  </div>

                                  <p className="card-text small text-muted">
                                    {task.childWorkplanDetails?.description ||
                                      "No description"}
                                  </p>

                                  {task.estimatedDuration && (
                                    <div className="d-flex align-items-center mb-2">
                                      <i className="ti ti-clock me-1"></i>
                                      <small>
                                        Est. {task.estimatedDuration} min
                                      </small>
                                    </div>
                                  )}

                                  {task.startTime && (
                                    <div className="d-flex align-items-center mb-2">
                                      <i className="ti ti-calendar-time me-1"></i>
                                      <small>
                                        {task.startTime} - {task.endTime}
                                      </small>
                                    </div>
                                  )}

                                  {task.notes && (
                                    <div className="alert alert-info py-1 px-2 mt-2">
                                      <small>{task.notes}</small>
                                    </div>
                                  )}

                                  <div className="mt-auto">
                                    <button
                                      className="btn btn-success btn-sm w-100"
                                      onClick={() =>
                                        handleTaskStatusUpdate(
                                          {
                                            _id: task.taskId,
                                            childWorkplanDetails:
                                              task.childWorkplanDetails,
                                            priority: task.priority,
                                          },
                                          {
                                            _id: task.dateId,
                                            status: task.status,
                                            notes: task.notes,
                                            startTime: task.startTime,
                                            endTime: task.endTime,
                                            completedBy: task.completedBy,
                                          },
                                        )
                                      }
                                      disabled={task.status === "completed"}
                                    >
                                      {task.status === "completed" ? (
                                        <>
                                          <i className="ti ti-check me-1"></i>
                                          Completed
                                        </>
                                      ) : (
                                        <>
                                          <i className="ti ti-check me-1"></i>
                                          Mark as Complete
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Task Update Modal */}
      {showTaskUpdateModal && selectedTask && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Update Task Status</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTaskUpdateModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Task</label>
                  <div className="card bg-light">
                    <div className="card-body p-2">
                      <strong>
                        {selectedTask.task.childWorkplanDetails?.name}
                      </strong>
                      <br />
                      <small className="text-muted">
                        Date:{" "}
                        {new Date(
                          selectedTask.dateInfo.date || new Date(),
                        ).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Status *</label>
                      <select
                        className="form-select"
                        value={taskUpdateData.status}
                        onChange={(e) =>
                          setTaskUpdateData((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="missed">Missed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Start Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={taskUpdateData.startTime}
                        onChange={(e) =>
                          setTaskUpdateData((prev) => ({
                            ...prev,
                            startTime: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">End Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={taskUpdateData.endTime}
                        onChange={(e) =>
                          setTaskUpdateData((prev) => ({
                            ...prev,
                            endTime: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    {taskUpdateData.status === "completed" && (
                      <div className="mb-3">
                        <label className="form-label">Completed By</label>
                        <div className="form-control bg-light">
                          {(() => {
                            // Get the staff user from the checklist
                            const staffId = checklist?.staffId;
                            const staffUser = staffUsers.find(
                              (user) => user._id === staffId,
                            );
                            return staffUser
                              ? staffUser.fullName || staffUser.name
                              : "Unknown User";
                          })()}
                        </div>
                        {/* Hidden input to store the actual staffId */}
                        <input
                          type="hidden"
                          value={checklist?.staffId || ""}
                          onChange={(e) =>
                            setTaskUpdateData((prev) => ({
                              ...prev,
                              completedBy: e.target.value,
                            }))
                          }
                        />
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={taskUpdateData.notes}
                        onChange={(e) =>
                          setTaskUpdateData((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        placeholder="Add any notes about this task..."
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowTaskUpdateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={submitTaskUpdate}
                  disabled={loading}
                >
                  {loading && (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  )}
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistManagementModal;
